import { useEffect, useRef } from 'react';
import type { Editor } from '../editor';
import { renderOverlay, renderScene, type OverlayState } from '../render/renderer';
import { onUnderlayLoaded } from '../render/underlayCache';
import { boxFromPoints } from '../model/geom';
import { entityGrips } from '../model/entity';

/**
 * 캔버스 (작업지시서 P2-02)
 *
 * 두 겹으로 겹쳐 놓는다:
 *   · main    — 도면. Scene/뷰포트가 바뀔 때만 다시 그린다.
 *   · overlay — 커서·스냅·미리보기. 매 프레임 다시 그린다.
 *
 * 다시 그리기는 requestAnimationFrame 으로 묶는다. 마우스 이동 이벤트는
 * 초당 수백 번 오는데 그때마다 그리면 프레임이 밀린다.
 */
export function CadCanvas({
  editor,
  onReady,
}: {
  editor: Editor;
  /**
   * 캔버스가 **실제 크기를 갖게 된 직후** 한 번 호출된다.
   *
   * 이게 필요한 이유: 화면 맞춤(zoomExtents)은 뷰포트 크기를 알아야 계산된다.
   * 부모 컴포넌트의 useEffect 에서 부르면 레이아웃이 아직 확정되지 않아
   * 기본 크기(800×600) 기준으로 배율이 잡히고, 실제 캔버스가 그보다 작으면
   * 도면 일부가 화면 밖으로 나간다. 실제로 겪은 버그다.
   */
  onReady?: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);

  // 다시 그릴 필요가 있는지 표시하는 플래그. rAF 루프가 읽어간다.
  const dirtyMain = useRef(true);
  const dirtyOverlay = useRef(true);
  /**
   * 화면 맞춤을 이미 해 준 Editor 인스턴스.
   *
   * 단순 boolean 래치를 쓰면 안 된다 — Editor 가 새로 만들어져도
   * (다른 버전 열기 · StrictMode 재생성 등) 이 컴포넌트는 그대로 살아 있어서
   * 래치가 true 로 남고, **새 뷰포트가 화면 맞춤을 영영 못 받는다.**
   * 실제로 겪은 버그이며, 증상은 "도면을 다시 열면 엉뚱한 배율로 뜬다" 이다.
   */
  const fittedFor = useRef<Editor | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const main = mainRef.current;
    const overlay = overlayRef.current;
    if (!wrap || !main || !overlay) return;

    const mainCtx = main.getContext('2d');
    const overlayCtx = overlay.getContext('2d');
    if (!mainCtx || !overlayCtx) return;

    const dpr = Math.min(2, window.devicePixelRatio || 1);

    const resize = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      for (const c of [main, overlay]) {
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
        c.style.width = `${w}px`;
        c.style.height = `${h}px`;
      }
      mainCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      editor.viewport.resize(w, h);
      dirtyMain.current = true;
      dirtyOverlay.current = true;

      // 이 Editor 에 대해 아직 화면 맞춤을 안 했고, 실제 크기를 얻었다면 알린다
      // (0×0 은 아직 레이아웃 전이므로 배율을 계산할 수 없다)
      if (fittedFor.current !== editor && w > 0 && h > 0) {
        fittedFor.current = editor;
        onReady?.();
      }
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    /* ----------------------------------------------------- 그리기 루프 */

    let raf = 0;
    const loop = () => {
      if (dirtyMain.current) {
        renderScene(mainCtx, editor.scene, editor.viewport, editor.selection);
        dirtyMain.current = false;
      }
      if (dirtyOverlay.current) {
        renderOverlay(overlayCtx, editor.viewport, buildOverlay(editor), editor.scene.blocks);
        dirtyOverlay.current = false;
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    /* ------------------------------------------------------- 구독 */

    const unsubEditor = editor.subscribe(() => {
      dirtyOverlay.current = true;
    });
    const unsubScene = editor.scene.subscribe(() => {
      dirtyMain.current = true;
      dirtyOverlay.current = true;
    });
    // 부착 이미지는 비동기로 로드된다 — 끝나면 메인 캔버스를 다시 그려야 보인다.
    const unsubUnderlay = onUnderlayLoaded(() => {
      dirtyMain.current = true;
    });

    /* ------------------------------------------------- 포인터 이벤트 */

    const local = (ev: PointerEvent | WheelEvent) => {
      const r = overlay.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    };

    let panLast: { x: number; y: number } | null = null;

    const onPointerMove = (ev: PointerEvent) => {
      const p = local(ev);
      if (panLast) {
        editor.panByPixels(p.x - panLast.x, p.y - panLast.y);
        panLast = p;
        dirtyMain.current = true;
      }
      editor.onPointerMove(p.x, p.y);
      dirtyOverlay.current = true;
    };

    const onPointerDown = (ev: PointerEvent) => {
      overlay.setPointerCapture(ev.pointerId);
      const p = local(ev);
      // 가운데 버튼 또는 Space+드래그 = 화면 이동
      if (ev.button === 1) {
        panLast = p;
        ev.preventDefault();
        return;
      }
      editor.onPointerDown(p.x, p.y, ev.button, ev.shiftKey);
      dirtyOverlay.current = true;
    };

    const onPointerUp = (ev: PointerEvent) => {
      const p = local(ev);
      if (panLast) {
        panLast = null;
        return;
      }
      editor.onPointerUp(p.x, p.y, ev.button, ev.shiftKey);
      dirtyMain.current = true;
      dirtyOverlay.current = true;
    };

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const p = local(ev);
      editor.onWheel(p.x, p.y, ev.deltaY);
      dirtyMain.current = true;
      dirtyOverlay.current = true;
    };

    // 우클릭 = Enter (AutoCAD 기본 동작)
    const onContextMenu = (ev: MouseEvent) => {
      ev.preventDefault();
      if (editor.activePrompt?.type === 'selection') editor.confirmSelection();
      else editor.feed({ t: 'enter' });
    };

    // 더블클릭 = 화면 전체 보기 (빠른 길잡이)
    const onDoubleClick = (ev: MouseEvent) => {
      if (editor.isRunning) return;
      ev.preventDefault();
      editor.zoomExtents();
      dirtyMain.current = true;
    };

    overlay.addEventListener('pointermove', onPointerMove);
    overlay.addEventListener('pointerdown', onPointerDown);
    overlay.addEventListener('pointerup', onPointerUp);
    overlay.addEventListener('wheel', onWheel, { passive: false });
    overlay.addEventListener('contextmenu', onContextMenu);
    overlay.addEventListener('dblclick', onDoubleClick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      unsubEditor();
      unsubScene();
      unsubUnderlay();
      overlay.removeEventListener('pointermove', onPointerMove);
      overlay.removeEventListener('pointerdown', onPointerDown);
      overlay.removeEventListener('pointerup', onPointerUp);
      overlay.removeEventListener('wheel', onWheel);
      overlay.removeEventListener('contextmenu', onContextMenu);
      overlay.removeEventListener('dblclick', onDoubleClick);
    };
  }, [editor, onReady]);

  return (
    <div className="cad-canvas-wrap" ref={wrapRef}>
      <canvas ref={mainRef} className="cad-canvas cad-canvas--main" />
      <canvas ref={overlayRef} className="cad-canvas cad-canvas--overlay" tabIndex={-1} />
    </div>
  );
}

function buildOverlay(editor: Editor): OverlayState {
  // 상자 선택 중이면 그 상자를 그린다
  let dragBox: OverlayState['dragBox'] = null;
  if (editor.dragMode === 'select' && editor.dragStartWorld && editor.dragCurrentWorld) {
    dragBox = {
      box: boxFromPoints(editor.dragStartWorld, editor.dragCurrentWorld),
      crossing: editor.dragCurrentWorld.x < editor.dragStartWorld.x,
    };
  }

  // 그립 — 선택이 많으면 그리지 않는다 (화면이 파란 점으로 뒤덮인다)
  const grips: OverlayState['grips'] = [];
  if (editor.selection.size > 0 && editor.selection.size <= 30) {
    for (const id of editor.selection) {
      const e = editor.scene.get(id);
      if (!e) continue;
      // 그립은 "끌 수 있는 점"이다. 스냅점과 다르다 —
      // 중점은 스냅은 되지만 끌 수는 없으므로 그립이 아니다.
      for (const g of entityGrips(e)) grips.push(g.point);
    }
  }

  return {
    cursor: editor.cursor,
    snap: editor.snapResult,
    preview: editor.preview,
    dragBox,
    grips,
    unit: editor.scene.doc.displayUnit,
  };
}
